<template>
  <v-container>
    <!-- ================================================================= -->
    <!-- HEADER                                                          -->
    <!-- ================================================================= -->
    <div class="d-flex align-center mb-4">
      <v-btn icon title="Zurück" @click="$router.go(-1)">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h1 class="ml-4 headline">Checkliste: {{ checklist.title }}</h1>
      <v-spacer></v-spacer>
      <v-btn color="success" large :loading="isSaving" @click="saveChecklist()">
        <v-icon left>mdi-content-save</v-icon> Checkliste speichern
      </v-btn>
    </div>

    <!-- ================================================================= -->
    <!-- MAIN ITEM LIST CARD                                             -->
    <!-- ================================================================= -->
    <v-card>
      <v-card-title>
        Checklistenpunkte
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="itemChoiceDialog = true">
          <v-icon left>mdi-plus</v-icon> Item hinzufügen
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-list two-line>
        <div v-if="!checklist.items || !checklist.items.length" class="text-center pa-4 grey--text">
          Dieser Checkliste wurden noch keine Items hinzugefügt.
        </div>

        <draggable v-model="checklist.items" handle=".drag-handle" ghost-class="ghost">
          <template v-for="(checklistItem, index) in checklist.items">
            <div :key="index">
              <v-list-item v-if="checklistItem && checklistItem.item">
                <v-list-item-action class="mr-4">
                  <v-icon class="drag-handle" style="cursor: move;" title="Sortieren">mdi-drag-vertical</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold d-flex align-center">
                    {{ checklistItem.item.title }}
                    <v-btn v-if="checklistItem.item.video_path" icon small class="ml-2" title="Video abspielen" @click="playVideo(checklistItem.item.video_path)">
                      <v-icon color="info">mdi-play-circle</v-icon>
                    </v-btn>
                  </v-list-item-title>
                  <v-list-item-subtitle v-html="checklistItem.item.body"></v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="d-flex flex-row align-center">
                  <v-text-field v-model.number="checklistItem.anzahl" label="Anzahl" type="number" min="1" style="width: 80px;" class="mr-4" outlined dense hide-details></v-text-field>
                  <v-btn icon title="Item bearbeiten" @click="editItem(checklistItem.item)"><v-icon color="secondary">mdi-pencil</v-icon></v-btn>
                  <v-btn icon title="Aus Liste entfernen" @click="removeItem(index)"><v-icon color="error">mdi-delete-outline</v-icon></v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index < checklist.items.length - 1"></v-divider>
            </div>
          </template>
        </draggable>
      </v-list>
    </v-card>

    <!-- ================================================================= -->
    <!-- DIALOGS                                                         -->
    <!-- ================================================================= -->
    <v-dialog v-model="itemChoiceDialog" max-width="500px">
      <v-card>
        <v-card-title>Item hinzufügen</v-card-title>
        <v-card-text class="text-center">
          <v-btn large color="primary" class="ma-2" @click="openLibrary()"><v-icon left>mdi-bookshelf</v-icon> Aus Bibliothek auswählen</v-btn>
          <v-btn large color="success" class="ma-2" @click="editItem(null)"><v-icon left>mdi-plus-box</v-icon> Neues Item erstellen</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="librarySelectDialog" max-width="800px">
      <v-card>
        <v-card-title>Item aus Bibliothek auswählen</v-card-title>
        <v-text-field v-model="search" label="Suchen..." clearable outlined dense hide-details class="ma-4"></v-text-field>
        <v-list style="max-height: 60vh; overflow-y: auto;">
          <v-list-item v-for="item in filteredMasterItems" :key="item._id" @click="addItemFromLibrary(item)">
            <v-list-item-content><v-list-item-title>{{ item.title }}</v-list-item-title></v-list-item-content>
            <v-list-item-action><v-icon color="primary">mdi-plus-circle-outline</v-icon></v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>

    <v-dialog v-model="itemEditorDialog" persistent max-width="900px">
      <v-card>
        <v-card-title><span class="headline">{{ isEditingItem ? 'Item bearbeiten' : 'Neues Item erstellen' }}</span></v-card-title>
        <v-card-text>
          <v-alert v-if="isEditingItem && otherUsage.length > 0" type="warning" dense border="left" class="mb-4">
            Dieses Item wird auch in <strong>{{ otherUsage.length }}</strong> anderen Checkliste(n) verwendet. Änderungen hier werden überall übernommen.
            <ul v-if="otherUsage.length < 5"><li v-for="usage in otherUsage" :key="usage._id">{{ usage.identifier }}</li></ul>
          </v-alert>
          <v-text-field v-model="editableItem.title" label="Titel" required outlined class="mb-4"></v-text-field>
          <vue-editor v-if="itemEditorDialog" v-model="editableItem.body" :editorOptions="editorSettings" useCustomImageHandler @image-added="handleImageAdded"></vue-editor>
          <v-divider class="my-4"></v-divider>
          <div class="subtitle-1 font-weight-bold">Video</div>
          <div v-if="isEditingItem && editableItem.video_path && !wantsToRemoveVideo" class="d-flex align-center pa-2 grey lighten-4 rounded">
            <v-icon class="mr-2">mdi-video</v-icon>
            <span class="text-truncate">{{ editableItem.video_path }}</span>
            <v-spacer></v-spacer>
            <v-btn text small color="red" @click="wantsToRemoveVideo = true">Video entfernen</v-btn>
          </div>
          <v-file-input v-else v-model="videoFileToUpload" label="Video hochladen/ersetzen" accept="video/*" outlined dense prepend-icon="mdi-video-plus"></v-file-input>
          <v-checkbox v-if="wantsToRemoveVideo" v-model="wantsToRemoveVideo" label="Video permanent entfernen" readonly></v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeItemEditor">Abbrechen</v-btn>
          <v-btn color="primary" text :loading="isSavingItem" @click="saveMasterItem">Speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="videoPlayerDialog" max-width="800px">
      <v-card>
        <v-card-title>Video</v-card-title>
        <v-card-text>
          <video v-if="videoPlayerDialog" :src="videoSource" controls style="width: 100%;"></video>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="videoPlayerDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.state" :timeout="snack.timeout" :color="snack.color" top right>{{ snack.message }}</v-snackbar>
  </v-container>
</template>

<style>
.ghost { opacity: 0.5; background: #c8ebfb; }
.ql-editor { min-height: 200px; }
.drag-handle { cursor: move; }
</style>

<script>
import axios from 'axios';
import draggable from 'vuedraggable';
import { VueEditor, Quill } from "vue2-editor";
import ImageResize from 'quill-image-resize-vue';

Quill.register("modules/imageResize", ImageResize);
const defaultItem = { title: '', body: '', video_path: null };

export default {
  name: "ChecklistCreator",
  components: { draggable, VueEditor },
  props: ['id'],
  data() {
    return {
      checklist: { title: '', items: [] },
      masterItems: [],
      search: '',
      isSaving: false,
      isSavingItem: false,
      itemChoiceDialog: false,
      librarySelectDialog: false,
      itemEditorDialog: false,
      videoPlayerDialog: false,
      isEditingItem: false,
      editableItem: { ...defaultItem },
      itemUsageList: [],
      videoFileToUpload: null,
      wantsToRemoveVideo: false,
      videoSource: '',
      editorSettings: { modules: { imageResize: {} } },
      snack: { state: false, color: 'success', timeout: 3000, message: '' },
    };
  },
  computed: {
    filteredMasterItems() {
      if (!this.search) return this.masterItems;
      return this.masterItems.filter(item => item.title.toLowerCase().includes(this.search.toLowerCase()));
    },
    otherUsage() {
      if (!this.itemUsageList) return [];
      return this.itemUsageList.filter(usage => usage._id !== this.id);
    }
  },
  mounted() {
    this.fetchChecklist();
    this.fetchMasterItems();
  },
  methods: {
    fetchChecklist() {
      axios.get(`/checklists/${this.id}`)
          .then(response => { this.checklist = response.data; })
          .catch(() => this.showSnackbar('Checkliste konnte nicht geladen werden.', 'error'));
    },
    fetchMasterItems() {
      axios.get('/items')
          .then(response => { this.masterItems = response.data; })
          .catch(() => this.showSnackbar('Item-Bibliothek konnte nicht geladen werden.', 'error'));
    },

    openLibrary() {
      this.itemChoiceDialog = false;
      this.librarySelectDialog = true;
    },

    addItemFromLibrary(item) {
      const isAlreadyAdded = this.checklist.items.some(i => i.item && i.item._id === item._id);
      if (isAlreadyAdded) {
        this.showSnackbar('Dieses Item ist bereits in der Checkliste.', 'warning');
        return;
      }
      this.checklist.items.push({ item: item, anzahl: 1 });
      this.librarySelectDialog = false;
      this.showSnackbar(`Item "${item.title}" hinzugefügt. Speichere...`, 'info');
      this.saveChecklist(false);
    },

    removeItem(index) {
      const itemTitle = this.checklist.items[index].item.title;
      this.checklist.items.splice(index, 1);
      this.showSnackbar(`Item "${itemTitle}" entfernt. Speichere...`, 'info');
      this.saveChecklist(false);
    },

    async editItem(item) {
      this.itemChoiceDialog = false;
      this.itemUsageList = [];
      this.closeItemEditor();

      if (item) {
        this.isEditingItem = true;
        this.editableItem = { ...item };
        try {
          const response = await axios.get(`/items/${item._id}/usage`);
          this.itemUsageList = response.data;
        } catch (e) { this.showSnackbar("Item-Verwendungsdaten konnten nicht geladen werden.", "error"); }
      } else {
        this.isEditingItem = false;
        this.editableItem = { ...defaultItem };
      }
      this.itemEditorDialog = true;
    },

    closeItemEditor() {
      this.itemEditorDialog = false;
      this.videoFileToUpload = null;
      this.wantsToRemoveVideo = false;
    },

    saveMasterItem() {
      this.isSavingItem = true;
      const formData = new FormData();
      formData.append('title', this.editableItem.title);
      formData.append('body', this.editableItem.body);
      if (this.videoFileToUpload) formData.append('video', this.videoFileToUpload);
      if (this.wantsToRemoveVideo) formData.append('remove_video', 'true');

      const promise = this.isEditingItem
          ? axios.patch(`/items/${this.editableItem._id}`, formData)
          : axios.post('/items', formData);

      promise.then(response => {
        this.showSnackbar('Item gespeichert.');
        this.closeItemEditor();
        this.fetchMasterItems();
        if (!this.isEditingItem) {
          this.addItemFromLibrary(response.data);
        } else {
          this.fetchChecklist();
        }
      }).catch(() => this.showSnackbar('Item konnte nicht gespeichert werden.', 'error'))
          .finally(() => this.isSavingItem = false);
    },

    saveChecklist(showNotification = true) {
      this.isSaving = true;
      const payload = {
        items: this.checklist.items
            .filter(i => i && i.item && i.item._id)
            .map(i => ({
              item: i.item._id,
              anzahl: i.anzahl
            }))
      };
      axios.patch(`/checklists/${this.id}`, payload)
          .then(response => {
            this.checklist = response.data;
            if (showNotification) {
              this.showSnackbar('Checkliste erfolgreich gespeichert!');
            }
          })
          .catch(() => this.showSnackbar('Fehler beim Speichern der Checkliste.', 'error'))
          .finally(() => this.isSaving = false);
    },

    handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      let formData = new FormData();
      formData.append("file", file);
      axios.post('/uploads/image', formData)
          .then(result => {
            const url = process.env.VUE_APP_STATIC_URL + '/uploads/' + result.data.url;
            Editor.insertEmbed(cursorLocation, "image", url);
            resetUploader();
          })
          .catch(() => {
            this.showSnackbar("Bild konnte nicht hochgeladen werden.", "error");
            resetUploader();
          });
    },

    playVideo(videoPath) {
      this.videoSource = process.env.VUE_APP_STATIC_URL + '/uploads/' + videoPath;
      this.videoPlayerDialog = true;
    },

    showSnackbar(message, color = 'success') {
      this.snack.message = message;
      this.snack.color = color;
      this.snack.state = true;
    }
  }
};
</script>